// src/components/Admin/AdminProfileSettingsSection.js (With Staff Message Feature)

import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../App';
import { api, BASE_URL } from '../../apiService';
import { toast } from 'react-toastify';

function AdminProfileSettingsSection() {
    const { 
        mainAppLogo, appTagline, gstEnabled, gstRate, invoiceTerms, bankDetails, staffLoginMessage,
        setMainAppLogo, setAppTagline, setGstEnabled, setGstRate, setInvoiceTerms, setBankDetails, setStaffLoginMessage
    } = useAppContext();


    
    const [newTagline, setNewTagline] = useState(appTagline);
    const [newLogoFile, setNewLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(mainAppLogo);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [localGstEnabled, setLocalGstEnabled] = useState(gstEnabled);
    const [localGstRate, setLocalGstRate] = useState(gstRate * 100);
    const [newInvoiceTerms, setNewInvoiceTerms] = useState(invoiceTerms);
    const [newBankDetails, setNewBankDetails] = useState(bankDetails);

    // --- NEW STATE FOR STAFF MESSAGE ---
    const [messageEnabled, setMessageEnabled] = useState(staffLoginMessage?.isEnabled || false);
    const [messageTitle, setMessageTitle] = useState(staffLoginMessage?.title || '');
    const [messageText, setMessageText] = useState(staffLoginMessage?.message || '');
    
    useEffect(() => {
        setNewTagline(appTagline);
        setLogoPreview(mainAppLogo);
        setLocalGstEnabled(gstEnabled);
        setLocalGstRate(gstRate * 100);
        setNewInvoiceTerms(invoiceTerms);
        setNewBankDetails(bankDetails);
        // Sync staff message form
        setMessageEnabled(staffLoginMessage?.isEnabled || false);
        setMessageTitle(staffLoginMessage?.title || '');
        setMessageText(staffLoginMessage?.message || '');
    }, [appTagline, mainAppLogo, gstEnabled, gstRate, invoiceTerms, bankDetails, staffLoginMessage]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) { setNewLogoFile(file); setLogoPreview(URL.createObjectURL(file)); }
    };
    
    const handleSaveChanges = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        try {
            let logoUrl = mainAppLogo;
            if (newLogoFile) {
                const formData = new FormData();
                formData.append('photo', newLogoFile);
                const response = await api.post('upload', formData, true);
                logoUrl = response.filePath;
            }
            
            const configDataToSave = {
                mainLogoUrl: logoUrl,
                tagline: newTagline,
                gstEnabled: localGstEnabled,
                gstRate: parseFloat(localGstRate) / 100,
                invoiceTerms: newInvoiceTerms,
                bankDetails: newBankDetails
            };

            // In a typical SQL setup, you might have a single row for config.
            // Here, we assume an endpoint that handles the update.
            const existing = await api.get('appConfig', { orderBy: 'id ASC', limit: 1 }).catch(() => []);
            const configId = existing?.[0]?.id;
            if (configId) await api.put('appConfig', configId, configDataToSave);
            else await api.post('appConfig', configDataToSave);

            // Update context to reflect changes instantly
            setMainAppLogo(logoUrl);
            setAppTagline(newTagline);
            setGstEnabled(localGstEnabled);
            setGstRate(parseFloat(localGstRate) / 100);
            setInvoiceTerms(newInvoiceTerms);
            setBankDetails(newBankDetails);
            const nextMessage = { isEnabled: messageEnabled, title: messageTitle, message: messageText };
            setStaffLoginMessage(nextMessage);
            localStorage.setItem('staffLoginMessage', JSON.stringify(nextMessage));

            toast.success("Settings updated successfully!");

        } catch (err) {
            toast.error(`Failed to save settings: ${err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    return (
        <div className="space-y-6">
            <div className="mb-8">
                <h1 className="text-4xl font-bold text-gray-800">Admin Settings</h1>
                <p className="text-gray-500 mt-1">Manage global settings for the application.</p>
            </div>
            <form onSubmit={handleSaveChanges} className="bg-white p-8 rounded-2xl shadow-lg space-y-8">
                {/* Branding and Financial Settings sections remain the same */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-6"><h3 className="text-lg font-bold text-gray-700 border-b pb-2">Branding</h3><div><label htmlFor="mainLogo" className="block text-sm font-bold text-gray-700">Main Application Logo</label><div className="mt-2 flex items-center gap-4"><img src={logoPreview.startsWith('blob:') || logoPreview.startsWith('http') ? logoPreview : `${BASE_URL}${logoPreview}`} alt="Current main logo" className="h-16 w-auto p-2 border rounded-md"/><label htmlFor="photo-upload" className="cursor-pointer bg-gray-100 text-gray-700 font-semibold py-2 px-4 rounded-lg hover:bg-gray-200">Change Logo</label><input type="file" id="photo-upload" onChange={handleFileChange} className="hidden" accept="image/*" /></div></div><div><label htmlFor="tagline" className="block text-sm font-bold text-gray-700">Application Tagline</label><input type="text" id="tagline" value={newTagline} onChange={e => setNewTagline(e.target.value)} className="mt-2 block w-full p-3 bg-gray-50 rounded-md"/></div></div>
                    <div className="bg-gray-50 p-6 rounded-xl border"><h3 className="text-lg font-bold text-gray-700 mb-4">Financial Settings</h3><div className="space-y-4"><div className="flex items-center"><input type="checkbox" id="gstEnabled" checked={localGstEnabled} onChange={e => setLocalGstEnabled(e.target.checked)} className="h-5 w-5 rounded text-rose-600"/><label htmlFor="gstEnabled" className="ml-3 font-medium text-gray-700">Enable GST on Invoices</label></div>{localGstEnabled && (<div><label htmlFor="gstRate" className="block text-sm font-medium text-gray-700">GST Rate (%)</label><input type="number" id="gstRate" value={localGstRate} onChange={e => setLocalGstRate(e.target.value)} className="mt-1 block w-full p-3 bg-white rounded-md"/></div>)}</div></div>
                </div>
                <div className="pt-6 border-t"><h3 className="text-lg font-bold text-gray-700 mb-4">Invoice Customization</h3><div className="grid grid-cols-1 md:grid-cols-2 gap-8"><div><label htmlFor="invoiceTerms" className="block text-sm font-bold text-gray-700">Terms & Conditions</label><textarea id="invoiceTerms" value={newInvoiceTerms} onChange={e => setNewInvoiceTerms(e.target.value)} rows="4" className="mt-2 block w-full p-3 bg-gray-50 rounded-md"/></div><div><label htmlFor="bankDetails" className="block text-sm font-bold text-gray-700">Bank Account Details</label><textarea id="bankDetails" value={newBankDetails} onChange={(e) => setNewBankDetails(e.target.value)} rows="4" className="mt-2 block w-full p-3 bg-gray-50 rounded-md"/></div></div></div>

                {/* --- NEW SECTION FOR STAFF MESSAGE --- */}
                <div className="pt-6 border-t">
                    <h3 className="text-lg font-bold text-gray-700 mb-4">Staff Announcement</h3>
                    <div className="bg-blue-50 p-6 rounded-xl border border-blue-200 space-y-4">
                        <div className="flex items-center">
                            <input type="checkbox" id="messageEnabled" checked={messageEnabled} onChange={e => setMessageEnabled(e.target.checked)} className="h-5 w-5 rounded text-rose-600 focus:ring-rose-500"/>
                            <label htmlFor="messageEnabled" className="ml-3 font-medium text-gray-700">Show a message to staff on login</label>
                        </div>
                        {messageEnabled && (
                            <>
                                <div>
                                    <label htmlFor="messageTitle" className="block text-sm font-bold text-gray-700">Message Title</label>
                                    <input type="text" id="messageTitle" value={messageTitle} onChange={e => setMessageTitle(e.target.value)} placeholder="e.g., Important Update" className="mt-2 block w-full p-3 bg-white rounded-md"/>
                                </div>
                                <div>
                                    <label htmlFor="messageText" className="block text-sm font-bold text-gray-700">Message Content</label>
                                    <textarea id="messageText" value={messageText} onChange={e => setMessageText(e.target.value)} rows="4" placeholder="Write your announcement here..." className="mt-2 block w-full p-3 bg-white rounded-md"/>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end">
                    <button type="submit" disabled={isSubmitting} className="bg-rose-600 text-white font-bold py-3 px-8 rounded-lg shadow-lg hover:bg-rose-700 transition-all disabled:opacity-50">
                        {isSubmitting ? "Saving..." : "Save All Settings"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default AdminProfileSettingsSection;
